import { regexErrorMessages, regexPatterns } from '../regexHelper';
import {
    articleLanguage,
    articleTitle,
    articleOpenInNewTab,
    articleCardImageAlt,
    articleIsClientSideRedirect,
    articleShowCardStatus,
    articleNoFollowStatus,
    articleIndexStatus,
    articleFeaturedStatus,
    articlePublishedStatus,
    articleHideMobileHeaderImageStatus,
    articleHeaderImageAlt,
    articleHeaderImage,
    articleMobileHeaderImage,
    articleMobileHeaderImageAlt,
    articleMobileCardImage,
    articleMobileCardImageAlt,
    articleCardImage,
    articleTag,
    articleCanonical,
    articleSummary,
    articlePageTitle,
    entityRedirectUrl,
    articleMetaDescription,
    articleSlug,
    slugEntityBreadcrumbDisplayName,
} from '@helpers/AccessibleFormHelperConstants';
import { breadcrumbDisplayNameValidator } from '@helpers/validationRules';
import { isNotEqual, regexMatchesOrIsEmpty } from '@helpers/validators';
import { ArticleFormHelper } from '@models/articleForm';
import { UserRole } from '@models/authentication';
import { EntityName } from '@models/entity';
import { PressRelease } from '@models/pressRelease';

const friendlyName = 'Press Release';
export const pressRoomFormHelper: ArticleFormHelper<PressRelease> = {
    name: 'Press Release',
    entityName: EntityName.PressReleaseArticle,
    apiControllerName: 'press-release',
    hasMediaField: false,
    isSubmitButtonLink: true,
    redirectLink: '/admin/press-release/edit/',
    redirectParam: true,
    gallerySelection: 'blogPageHeaderImage',
    fields: [
        {
            id: 'press-release-language',
            key: 'language',
            name: 'Language',
            required: true,
            type: 'select',
            list: 'languages',
            listItemValueKey: 'id',
            listFilterCallback: (list) => {
                return list.filter((language) => language.routes?.pressReleaseUrl);
            },
            hasLanguageUpdate: true, // Flag used for temp fix - WFM-8007
            readOnlyModes: ['edit'],
            updateFieldsOnChange: [{ fieldKey: 'tags', newValue: [] }],
            formHelper: articleLanguage(friendlyName),
        },
        {
            id: 'press-release-title',
            key: 'title',
            name: 'Press Release Title',
            required: true,
            type: 'text',
            formHelper: articleTitle(friendlyName),
        },
        {
            id: 'press-release-pageTitle',
            key: 'pageTitle',
            name: 'Page Title',
            type: 'text',
            formHelper: articlePageTitle(friendlyName),
        },
        {
            id: 'press-release-metaDescription',
            key: 'metaDescription',
            name: 'Meta Description',
            validators: [isNotEqual('description', 'Meta Description should be unique from Description')],
            fieldsToRevalidate: ['press-release-description'],
            required: true,
            maxLength: 160,
            type: 'text',
            formHelper: articleMetaDescription(friendlyName),
        },
        {
            id: 'press-release-slug',
            key: 'slug',
            name: 'Url Slug',
            validators: [regexMatchesOrIsEmpty(regexPatterns.slug, regexErrorMessages.slug)],
            required: true,
            maxLength: 100,
            type: 'text',
            sanitizationConfiguration: {
                autoTrim: true,
                lowercaseOnChange: true,
            },
            formHelper: articleSlug(friendlyName),
        },
        {
            id: 'press-release-canonical',
            key: 'canonical',
            name: 'Canonical Url',
            required: false,
            maxLength: 5000,
            type: 'text',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            formHelper: articleCanonical(friendlyName),
        },
        {
            id: 'press-release-redirectUrl',
            key: 'redirectUrl',
            name: 'Redirect Url',
            required: false,
            maxLength: 5000,
            type: 'text',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            formHelper: entityRedirectUrl(friendlyName),
        },
        {
            id: 'press-release-description',
            key: 'description',
            name: 'Description',
            validators: [isNotEqual('metaDescription', 'Description should be unique from Meta Description')],
            fieldsToRevalidate: ['press-release-metaDescription'],
            required: false,
            maxLength: 300,
            type: 'text',
            formHelper: articleSummary(friendlyName),
        },
        {
            id: 'press-release-breadcrumbDisplayName',
            key: 'breadcrumbDisplayName',
            name: 'Breadcrumb Display Name',
            validators: [breadcrumbDisplayNameValidator],
            required: false,
            type: 'text',
            formHelper: slugEntityBreadcrumbDisplayName(friendlyName),
        },
        {
            id: 'press-release-tags',
            key: 'tags',
            name: 'Tags',
            required: false,
            type: 'multiTagSelect',
            list: 'tags',
            listFilterOnField: 'language',
            listFilterListItemKey: 'language',
            hiddenTagHelperLabel: true,
            formHelper: articleTag(friendlyName),
        },
        {
            id: 'press-release-cardImageUrl',
            key: 'cardImageUrl',
            name: 'Card Image',
            required: false,
            type: 'galleryImage',
            gallerySelection: 'cardImage',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            ratio: ['4:3'],
            maxWidth: 960,
            formHelper: articleCardImage(friendlyName),
            fieldsToRevalidate: ['press-release-cardImageAlt'],
            fieldKeyToUpdate: 'cardImageAlt',
        },
        {
            id: 'press-release-cardImageAlt',
            key: 'cardImageAlt',
            name: 'Card Image Alt',
            requiredCondition: (entity: PressRelease) => !!entity.cardImageUrl,
            maxLength: 300,
            type: 'text',
            formHelper: articleCardImageAlt(friendlyName),
        },
        {
            id: 'press-release-mobileCardImageUrl',
            key: 'mobileCardImageUrl',
            name: 'Mobile Card Image',
            required: false,
            type: 'galleryImage',
            gallerySelection: 'mobileCardImage',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            ratio: ['4:3'],
            maxWidth: 960,
            formHelper: articleMobileCardImage(friendlyName),
            fieldsToRevalidate: ['press-release-cardImageAlt'],
        },
        {
            id: 'press-release-mobileCardImageAlt',
            key: 'mobileCardImageAlt',
            name: 'Mobile Card Image Alt',
            requiredCondition: (entity: PressRelease) => !!entity.mobileCardImageUrl,
            maxLength: 300,
            type: 'text',
            formHelper: articleMobileCardImageAlt(friendlyName),
        },
        {
            id: 'press-release-headerImageUrl',
            key: 'imageUrl',
            name: 'Header Image',
            required: false,
            type: 'galleryImage',
            gallerySelection: 'articleImage',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            ratio: ['16:9'],
            maxWidth: 1920,
            formHelper: articleHeaderImage(friendlyName),
            fieldsToRevalidate: ['press-release-headerImageAlt'],
            fieldKeyToUpdate: 'imageAlt',
        },
        {
            id: 'press-release-headerImageAlt',
            key: 'imageAlt',
            name: 'Header Image Alt',
            requiredCondition: (entity: PressRelease) => !!entity.imageUrl,
            maxLength: 300,
            type: 'text',
            formHelper: articleHeaderImageAlt(friendlyName),
        },
        {
            id: 'press-release-mobileHeaderImageUrl',
            key: 'mobileImageUrl',
            name: 'Mobile Header Image',
            required: false,
            type: 'galleryImage',
            gallerySelection: 'mobileImageUrl',
            sanitizationConfiguration: {
                autoTrim: true,
            },
            ratio: ['1:1', '4:3'],
            maxWidth: 960,
            formHelper: articleMobileHeaderImage(friendlyName),
            fieldsToRevalidate: ['press-release-headerImageAlt'],
        },
        {
            id: 'press-release-mobileHeaderAlt',
            key: 'mobileHeaderAlt',
            name: 'Mobile Header Image Alt',
            requiredCondition: (entity: PressRelease) => !!entity.mobileImageUrl,
            maxLength: 300,
            type: 'text',
            formHelper: articleMobileHeaderImageAlt(friendlyName),
        },
        {
            id: 'press-release-hideMobileHeaderImage',
            key: 'hideMobileHeaderImage',
            name: 'Hide Mobile Header Image',
            type: 'checkbox',
            formHelper: articleHideMobileHeaderImageStatus(friendlyName),
        },
        {
            id: 'press-release-publishedDateUtc',
            key: 'publishedDateUtc',
            name: 'Published',
            type: 'publishBox',
            formHelper: articlePublishedStatus(friendlyName),
            modifiableByRoles: [UserRole.GeotabComCmsAdmin, UserRole.GeotabComArticleAdmin],
        },
        {
            id: 'press-release-newTab',
            key: 'newTab',
            name: 'New Tab',
            type: 'checkbox',
            formHelper: articleOpenInNewTab(friendlyName),
        },
        {
            id: 'press-release-featured',
            key: 'featured',
            name: 'Featured',
            type: 'checkbox',
            formHelper: articleFeaturedStatus(friendlyName),
        },
        {
            id: 'press-release-noIndex',
            key: 'noIndex',
            name: 'No Index',
            type: 'checkbox',
            formHelper: articleIndexStatus(friendlyName),
        },
        {
            id: 'press-release-noFollow',
            key: 'noFollow',
            name: 'No Follow',
            type: 'checkbox',
            formHelper: articleNoFollowStatus(friendlyName),
        },
        {
            id: 'press-release-visible',
            key: 'visible',
            name: 'Show Card',
            type: 'checkbox',
            formHelper: articleShowCardStatus(friendlyName),
        },
        {
            id: 'press-release-isClientSideRedirect',
            key: 'isClientSideRedirect',
            name: 'Client-Side Redirect',
            type: 'checkbox',
            formHelper: articleIsClientSideRedirect(friendlyName),
        },
        {
            id: 'press-release-hreflangGroupName',
            key: 'hreflangGroupName',
            name: 'Href-lang Group',
            type: 'labelText',
        },
    ],
};

export const pressRoomModel: Partial<PressRelease> = {
    title: '',
    pageTitle: '',
    metaDescription: '',
    description: '',
    slug: '',
    canonical: '',
    redirectUrl: '',
    tags: [],
    cardImageAlt: '',
    language: 1,
    imageAlt: '',
    videoUrl: null,
    featured: false,
    noFollow: false,
    noIndex: false,
    newTab: false,
    visible: true,
    isClientSideRedirect: false,
};
